"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  CARD,
  TABLE,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  chunkRows,
  EASY_GRADER_MAX_TOTAL,
  type EasyGraderRow,
  easyGraderRows,
} from "@/lib/tool-kit/grades/easy-grader";
import {
  LETTER_SCALE_PRESETS,
  type LetterBand,
  type LetterScalePreset,
  letterFor,
} from "@/lib/tool-kit/grades/letter-scales";
import type { RoundingMode } from "@/lib/tool-kit/grades/rounding";

/** English strings. A translated content file overrides these through `ui`. */
export const EASY_GRADER_STRINGS = {
  countByLabel: "Count by",
  countQuestions: "Questions",
  countPoints: "Points",
  totalQuestionsLabel: "Number of questions",
  totalPointsLabel: "Total points",
  halfLabel: "Allow half points (steps of 0.5)",
  decimalsLabel: "Decimal places",
  decimals0: "Whole numbers",
  decimals1: "One decimal place",
  decimals2: "Two decimal places",
  roundingLabel: "Rounding",
  roundNearest: "To the nearest (86.7 → 87)",
  roundDown: "Down (86.7 → 86)",
  roundUp: "Up (86.2 → 87)",
  scaleLabel: "Letter grades",
  scalePlusMinus: "Plus/minus — A 93, A− 90, B+ 87 … D− 60",
  scalePlusMinusAPlus: "Plus/minus with A+ from 97",
  scaleTenPoint: "10-point A–F — A 90, B 80, C 70, D 60",
  print: "Print chart",
  chartTitleQuestions:
    "one: Score chart for {count} question|other: Score chart for {count} questions",
  chartTitlePoints:
    "one: Score chart for {count} point|other: Score chart for {count} points",
  colWrong: "Wrong",
  colPointsOff: "Points off",
  colScore: "Score %",
  colGrade: "Grade",
  enterTotal: "Enter a whole number from 1 to {max}.",
  chartNote:
    "Score = (total − wrong) ÷ total × 100. Each letter follows the rounded score shown beside it.",
} as const;

type Strings = typeof EASY_GRADER_STRINGS;
type CountBy = "questions" | "points";
type Decimals = "0" | "1" | "2";

/** At most three columns, at least ten rows in each — a slide chart's proportions. */
function columnSize(rows: number): number {
  const columns = Math.min(3, Math.max(1, Math.ceil(rows / 10)));
  return Math.ceil(rows / columns);
}

/** Chart cells: the kit's table, tightened so three columns fit beside each other. */
const CHART_TH =
  "whitespace-nowrap border-border border-b px-2 py-2 font-medium text-foreground text-xs uppercase tracking-wide";
const CHART_TD = "border-border border-b px-2 py-1.5 text-foreground";

function ChartColumn({
  rows,
  bands,
  countBy,
  decimals,
  strings,
  fmt,
}: {
  rows: readonly EasyGraderRow[];
  bands: readonly LetterBand[];
  countBy: CountBy;
  decimals: number;
  strings: Strings;
  fmt: ReturnType<typeof useToolNumber>;
}) {
  return (
    <table className={`${TABLE} break-inside-avoid`}>
      <thead>
        <tr>
          <th className={CHART_TH} scope="col">
            {countBy === "questions" ? strings.colWrong : strings.colPointsOff}
          </th>
          <th className={CHART_TH} scope="col">
            {strings.colScore}
          </th>
          <th className={CHART_TH} scope="col">
            {strings.colGrade}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.wrong}>
            <td className={`${CHART_TD} font-mono`}>
              {fmt(row.wrong, { maximumFractionDigits: 1 })}
            </td>
            <td className={`${CHART_TD} font-mono font-semibold`}>
              {fmt(row.score, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
              })}
            </td>
            <td className={CHART_TD}>
              {letterFor(row.score, bands)?.letter ?? ""}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function EasyGraderWidget() {
  const strings = useToolUi(EASY_GRADER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [countBy, setCountBy] = useState<CountBy>("questions");
  const [totalRaw, setTotalRaw] = useState("30");
  const [half, setHalf] = useState(false);
  const [decimals, setDecimals] = useState<Decimals>("0");
  const [rounding, setRounding] = useState<RoundingMode>("nearest");
  const [scale, setScale] = useState<LetterScalePreset>("plus-minus");

  const total = parseIntegerInput(totalRaw);
  const valid = total !== null && total >= 1 && total <= EASY_GRADER_MAX_TOTAL;
  const places = Number(decimals);
  const rows = valid
    ? easyGraderRows(total, half ? 0.5 : 1, places, rounding)
    : [];
  const columns = chunkRows(rows, columnSize(rows.length));
  const bands = LETTER_SCALE_PRESETS[scale];
  const title = tPlural(
    countBy === "questions"
      ? strings.chartTitleQuestions
      : strings.chartTitlePoints,
    locale,
    total ?? 0,
    { count: fmt(total ?? 0) }
  );

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5 print:hidden`}>
        <PillGroup
          label={strings.countByLabel}
          name="ezg-count-by"
          onChange={setCountBy}
          options={[
            { value: "questions", label: strings.countQuestions },
            { value: "points", label: strings.countPoints },
          ]}
          value={countBy}
        />
        <TextField
          id="ezg-total"
          label={
            countBy === "questions"
              ? strings.totalQuestionsLabel
              : strings.totalPointsLabel
          }
          numeric
          onChange={setTotalRaw}
          value={totalRaw}
        />
        <Checkbox checked={half} label={strings.halfLabel} onChange={setHalf} />
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="ezg-decimals"
            label={strings.decimalsLabel}
            onChange={setDecimals}
            options={[
              { value: "0", label: strings.decimals0 },
              { value: "1", label: strings.decimals1 },
              { value: "2", label: strings.decimals2 },
            ]}
            value={decimals}
          />
          <SelectField
            id="ezg-rounding"
            label={strings.roundingLabel}
            onChange={setRounding}
            options={[
              { value: "nearest", label: strings.roundNearest },
              { value: "down", label: strings.roundDown },
              { value: "up", label: strings.roundUp },
            ]}
            value={rounding}
          />
          <SelectField
            id="ezg-scale"
            label={strings.scaleLabel}
            onChange={setScale}
            options={[
              { value: "plus-minus", label: strings.scalePlusMinus },
              {
                value: "plus-minus-a-plus",
                label: strings.scalePlusMinusAPlus,
              },
              { value: "ten-point", label: strings.scaleTenPoint },
            ]}
            value={scale}
          />
        </div>
      </div>

      {valid ? (
        <section aria-live="polite" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className={WIDGET_H2}>{title}</h2>
            <button
              className={`${BUTTON_PRIMARY} print:hidden`}
              onClick={() => window.print()}
              type="button"
            >
              {strings.print}
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 print:grid-cols-3">
            {columns.map((column) => (
              <div
                className="overflow-x-auto rounded-lg border border-border bg-card print:border-black"
                key={column[0]?.wrong ?? 0}
              >
                <ChartColumn
                  bands={bands}
                  countBy={countBy}
                  decimals={places}
                  fmt={fmt}
                  rows={column}
                  strings={strings}
                />
              </div>
            ))}
          </div>
          <p className="text-muted-foreground text-xs print:text-black">
            {strings.chartNote}
          </p>
        </section>
      ) : (
        <Notice>
          {t(strings.enterTotal, { max: fmt(EASY_GRADER_MAX_TOTAL) })}
        </Notice>
      )}
    </div>
  );
}
